'use client';

import {
  useCartStore,
  useShallow,
  DotSpinner,
  Container,
  CustomImage,
} from '..';
import Image from 'next/image';
import { useState } from 'react';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  nightTransparentGlassesShopifyMap,
  SleepMaskShopifyMap,
} from 'content/products/accessories';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';

const HERO_IMAGE = getCompressedAssetUrl(
  '/web_v2/site-static/home/heroImage.png',
);
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { DiscountPill } from 'components/shared/DiscountPill';
import { ShopCardPrimaryCta } from 'components/products/shopProductCard';
import {
  getBlingDiscountPercentage,
  isBlingVisible,
  RingStackType,
} from 'content/products/ring/stacking/constants';
import {
  getStackingPrice,
  StackingShopifyMap,
} from 'content/products/ring/stacking/StackingShopifyMap';
import styles from './HeroBanner.module.css';

const cx = (...c: (string | undefined | false)[]) => c.filter(Boolean).join(' ');

// A `?discount=` code carries its percentage in the last two characters (e.g. BDAY20).
const DEFAULT_BIRTHDAY_DISCOUNT_PERCENT = 15;

export const FeaturedGlass = ({ className }: { className?: string }) => {
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      addToCart: s.addToCart,
      currency: s.currency,
      taxString: s.taxString,
    })),
  );
  const [isLoading, setIsLoading] = useState(false);
  const formatPriceWithComma = useFormatPrice();
  const t = useTranslations('ring');
  const tCart = useTranslations('cart');

  const handleCart = async () => {
    const variantId = nightTransparentGlassesShopifyMap?.[
      CartClient.store as ShopifyStore
    ]?.shopifyVariantId as string;

    if (!variantId) return;

    try {
      setIsLoading(true);
      await CartClient.addToCart([
        {
          shopifyVariantId: variantId,
          productType: ProductType.MERCH,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cx('bannerImgSectionContainer', styles.featuredBanner, className)}
    >
      <div
        className="overlay"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      ></div>
      <div className="bannerImgSection">
        <Image
          src={HERO_IMAGE}
          alt="banner_img"
          width={450}
          height={264}
          unoptimized
        />
        <div className="shadow" />
      </div>

      <div className="productInfo">
        <div className="newlyLaunched">{t('ring.heroBanner.newlyLaunched')}</div>

        <div className="info">
          <div className="ringSpec">
            {t('ring.heroBanner.blueLightBlocking')}
            <br />
            <div className="pricing">
              {
                CurrencySymbol[
                  (CartClient.currency ?? Currency.usd) as Currency
                ]
              }
              {formatPriceWithComma(
                nightTransparentGlassesShopifyMap?.[
                  CartClient.store as ShopifyStore
                ]?.price as number,
              )}
              <span> {CartClient.taxString} </span>
            </div>
          </div>

          <div className="cartCta">
            <button
              className="add gtm"
              data-buttontype="add to cart"
              data-variantId={
                nightTransparentGlassesShopifyMap?.[
                  CartClient.store as ShopifyStore
                ]?.shopifyVariantId
              }
              data-product="Blue Light Blocking Glasses"
              onClick={handleCart}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              <p>
                {isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : (
                  tCart('sharedCart.upsell.addToCart')
                )}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeaturedSleepMask = ({
  className,
}: {
  className?: string;
}) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        addToCart: s.addToCart,
        currency: s.currency,
        taxString: s.taxString,
      })),
    );
    const [isLoading, setIsLoading] = useState(false);
    const formatPriceWithComma = useFormatPrice();
    const t = useTranslations('ring');
    const tCart = useTranslations('cart');

    const handleCart = async () => {
      const variantId = SleepMaskShopifyMap?.[CartClient.store as ShopifyStore]
        ?.shopifyVariantId as string;

      if (!variantId) return;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: ProductType.MERCH,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div
        className={cx('bannerImgSectionContainer', styles.featuredBanner, className)}
      >
        <div
          className="overlay"
          style={{
            backgroundImage: `url(${getCompressedAssetUrl(
              '/web_v2/all-products/accessories/sleepmask_2.jpg',
            )})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        ></div>
        <div className="bannerImgSection">
          <Image
            src={getCompressedAssetUrl(
              '/web_v2/all-products/accessories/sleepmask_2.jpg',
            )}
            width="450"
            height="250"
            alt="banner_img"
          />
          <div className="shadow" />
        </div>

        <div className="productInfo">
          <div className="info">
            <div className="ringSpec">
              {t('ring.heroBanner.blueLightBlocking')}
              <br />
              <div className="pricing">
                {
                  CurrencySymbol[
                    (CartClient.currency ?? Currency.usd) as Currency
                  ]
                }
                {formatPriceWithComma(
                  nightTransparentGlassesShopifyMap?.[
                    CartClient.store as ShopifyStore
                  ]?.price as number,
                )}
                <span> {CartClient.taxString} </span>
              </div>
            </div>

            <div className="cartCta">
              <button
                className="add gtm"
                data-buttontype="add to cart"
                data-variantId={
                  nightTransparentGlassesShopifyMap?.[
                    CartClient.store as ShopifyStore
                  ]?.shopifyVariantId
                }
                data-product="Blue Light Blocking Glasses"
                onClick={handleCart}
                disabled={isLoading}
                aria-busy={isLoading}
              >
                <p>
                  {isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : (
                  tCart('sharedCart.upsell.addToCart')
                )}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
};

export const FeaturedSleepingMask = ({
  className,
}: {
  className?: string;
}) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        addToCart: s.addToCart,
        currency: s.currency,
        taxString: s.taxString,
      })),
    );
    const [isLoading, setIsLoading] = useState(false);
    const formatPriceWithComma = useFormatPrice();
    const t = useTranslations('ring');
    const tCart = useTranslations('cart');

    const handleCart = async () => {
      const variantId = SleepMaskShopifyMap?.[CartClient.store as ShopifyStore]
        ?.shopifyVariantId as string;

      if (!variantId) return;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: ProductType.MERCH,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div
        className={cx('bannerImgSectionContainer', styles.featuredSleepingMask, className)}
      >
        <div
          className="overlay"
          style={{
            backgroundImage: `url(${getCompressedAssetUrl(
              '/web_v2/all-products/accessories/BLBG.png/w_1920',
            )})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        ></div>
        <div className="bannerImgSection">
          <Image
            src={HERO_IMAGE}
            alt="banner_img"
            width={450}
            height={264}
            style={{ backgroundColor: '#ffffff' }}
            unoptimized
          />
          <div className="shadow" />
        </div>

        <div className="productInfo">
          <div className="info">
            <div className="ringSpec">
              {t('ring.shop.products.sleepMask.title')}
              <br />
              <div className="pricing">
                {
                  CurrencySymbol[
                    (CartClient.currency ?? Currency.usd) as Currency
                  ]
                }
                {formatPriceWithComma(
                  SleepMaskShopifyMap?.[CartClient.store as ShopifyStore]
                    ?.price as number,
                )}
                <span> {CartClient.taxString} </span>
              </div>
            </div>

            <div className="cartCta">
              <button
                className="add gtm"
                data-buttontype="add to cart"
                data-variantId={
                  SleepMaskShopifyMap?.[CartClient.store as ShopifyStore]
                    ?.shopifyVariantId
                }
                data-product="Blue Light Blocking Glasses"
                onClick={handleCart}
                disabled={isLoading}
                aria-busy={isLoading}
              >
                <p>
                  {isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : (
                  tCart('sharedCart.upsell.addToCart')
                )}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
};

export const FeaturedBling = ({ className }: { className?: string }) => {
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      currency: s.currency,
      taxString: s.taxString,
    })),
  );
  const { region, store: regionStore } = useRegionLocale();
  const formatPriceWithComma = useFormatPrice();
  const t = useTranslations('ring');

  const store = (CartClient.store ?? regionStore) as ShopifyStore;
  const discountRate = getBlingDiscountPercentage(store);
  const originalPrice =
    StackingShopifyMap[store]?.[RingStackType.SILVER_HALO]?.price ?? 0;
  const price = getStackingPrice(store, RingStackType.SILVER_HALO);

  if (!isBlingVisible(region, store) || discountRate <= 0 || !originalPrice) {
    return null;
  }

  return (
    <div className={cx(styles.blingBanner, className)}>
      <div className={styles.blingBannerFrame}>
        <CustomImage
          src="web_v2/hero/bling-hero.png"
          alt={t('ring.shop.blingBannerAlt')}
          fill
          sizes="466px"
        />
        <div className={styles.blingBannerContent}>
          <div className={styles.blingTextCol}>
            <div className={styles.blingTitle}>
              {t('ring.productSelection.stacking.sectionTitle')}
              {t('ring.productSelection.stacking.sectionSubTitle')}
            </div>
            <span className={styles.blingStartsFrom}>
              {t('ring.productSelection.stacking.startsFrom')}
            </span>
            <div className={styles.blingPricing}>
              <span className={styles.featuredStrikedPrice}>
                {CurrencySymbol[(CartClient.currency ?? Currency.usd) as Currency]}
                {formatPriceWithComma(originalPrice)}
              </span>
              {CurrencySymbol[(CartClient.currency ?? Currency.usd) as Currency]}
              {formatPriceWithComma(price)}
              <span className={styles.blingTaxNote}> {CartClient.taxString} </span>
            </div>
            <DiscountPill>
              {t('ring.productSelection.stacking.discountTag', {
                percent: discountRate,
              })}
            </DiscountPill>
          </div>
          <ShopCardPrimaryCta
            className={cx('gtm', styles.blingCta)}
            data-buttontype="shop bling"
            data-product="Ultrahuman Bling"
            onClick={() =>
              document
                .getElementById('addons')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            {t('ring.productSelection.stacking.shopCta')}
          </ShopCardPrimaryCta>
        </div>
      </div>
    </div>
  );
};

export const HeroBanner = ({
  className,
  title,
  description,
}: {
  className?: string;
  title?: string | React.ReactNode;
  description?: string;
}) => {
    const t = useTranslations('ring');
    const searchParams = useSearchParams();

    const discount = searchParams?.get('discount') || null;
    const birthdayDiscountPercentage = discount
      ? parseInt(discount.slice(-2)) || DEFAULT_BIRTHDAY_DISCOUNT_PERCENT
      : 0;
    const isBirthdayDiscountActive =
      discount &&
      discount.length > 0 &&
      searchParams?.get('affiliateCode') === 'birthday';

    return (
      <section className={cx(styles.heroBanner, className)}>
        <Container
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <div className="content">
            <div className="head" id="h">
              {isBirthdayDiscountActive
                ? t('ring.shop.birthdayOffer', {
                    percentage: birthdayDiscountPercentage,
                  })
                : (title ?? t('ring.shop.oneStopShop'))}
            </div>
            {description ? <div className="sub-head">{description}</div> : null}
          </div>
          <FeaturedBling />
        </Container>
      </section>
    );
};
